import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AuthLayout } from '@/components/layout/auth-layout';
import { apiClient, type EnableMfaResponse } from '@/lib/api-client';

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

/**
 * Setup do MFA: usuario autenticado chama mfaSetup -> recebe secret/otpauthUrl/recoveryCodes,
 * copia secret pro app autenticador, confirma com codigo de 6 digitos via mfaVerifySetup.
 *
 * QR code: renderizamos otpauthUrl via servico Google Charts (sem dependencia extra
 * agora; substituir por qrcode.react quando feature for priorizada).
 */
export default function MfaSetupPage() {
  const [setup, setSetup] = useState<EnableMfaResponse | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function startSetup() {
    setError(null);
    setLoading(true);
    try {
      const res = await apiClient.mfaSetup();
      setSetup(res);
    } catch (err) {
      setError(extractApiMessage(err, 'Falha ao iniciar MFA'));
    } finally {
      setLoading(false);
    }
  }

  async function confirmCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiClient.mfaVerifySetup(code);
      setDone(true);
    } catch (err) {
      setError(extractApiMessage(err, 'Código inválido'));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>MFA ativado</CardTitle>
            <CardDescription>
              Da próxima vez que entrar, será pedido um código de 6 dígitos.
            </CardDescription>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Configurar MFA (TOTP)</CardTitle>
          <CardDescription>
            Use Google Authenticator, Authy ou 1Password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!setup ? (
            <Button onClick={startSetup} disabled={loading} className="w-full">
              {loading ? 'Carregando...' : 'Gerar secret'}
            </Button>
          ) : (
            <>
              <div className="space-y-2">
                <Label>QR code</Label>
                <div className="flex justify-center rounded border border-[var(--border)] bg-white p-3">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauthUrl)}`}
                    alt="QR code MFA"
                    width={180}
                    height={180}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Secret (caso o QR não funcione)</Label>
                <p className="font-mono break-all rounded border border-[var(--border)] bg-[var(--bg-elev)] p-2 text-sm text-[var(--fg)]">
                  {setup.secret}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Códigos de recuperação (guarde agora!)</Label>
                <ul className="rounded border border-[var(--border)] bg-[var(--bg-elev)] p-2 font-mono text-sm text-[var(--fg)]">
                  {setup.recoveryCodes.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
              <form onSubmit={confirmCode} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="code">Código do app (6 dígitos)</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Verificando...' : 'Confirmar'}
                </Button>
              </form>
            </>
          )}
          {error && !setup && <p className="text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
